// The pull request page's writes, sent as effects of its Program
// (pr/program.ts, `write`): each in a lane of its own, recorded in the
// account's invalidation log as an intent before it goes and done once
// answered. The store's pending writes (pending.svelte.ts) hand a send
// over with `perform`; the message carries the callback its answer
// settles, so a write is sent exactly once whenever the Program takes it.
import type { Outcome } from '../../runtime/effects';
import { logKind, WriteAbandoned, type WriteKind } from '../pending.svelte';
import type { Msg } from './program';

export class Outbox {
  /** The writes handed to the Program and not answered yet. */
  private waiting = new Set<(o: Outcome<unknown>) => void>();

  /** `send` tells the Program; `stopped` says the store was disposed (its Program takes nothing more). */
  constructor(
    private readonly send: (m: Msg) => void,
    private readonly stopped: () => boolean,
  ) {}

  /**
   * Send a write of `kind` through the Program; settles with its answer.
   * Once the store is disposed the write is sent directly instead, so
   * nothing the user asked for is dropped unsent.
   */
  perform<T>(kind: WriteKind, send: () => Promise<T>): Promise<T> {
    if (this.stopped()) return send();
    return new Promise<T>((resolve, reject) => {
      const settle = (o: Outcome<unknown>) => {
        if (!this.waiting.delete(settle)) return;
        if (o.ok) resolve(o.value as T);
        else reject(o.error);
      };
      this.waiting.add(settle);
      this.send({ t: 'write', kind: logKind(kind), send, settle });
    });
  }

  /** The Program started over (another account signed in): the writes in flight are never answered, so they are given up here. */
  abandon(): void {
    for (const settle of [...this.waiting]) settle({ ok: false, error: new WriteAbandoned() });
  }

  /** How many writes wait for their answer. */
  get size(): number {
    return this.waiting.size;
  }
}
